import { Injectable } from '@angular/core';
import {
  AccountGroup,
  AccountGroupService,
  LookupFilter,
  ProjectionFilter,
  SnapshotFilter,
} from '@galaxy/account-group';
import { combineLatest, Observable, of } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { BusinessCenterService, VBCEntryInfo } from '../core/business-center.service';

export interface AccountInfo {
  accountGroup: AccountGroup;
  vbcEntryInfo?: VBCEntryInfo;
}

@Injectable()
export class AccountLookupService {
  constructor(
    private readonly accountGroupService: AccountGroupService,
    private readonly businessCenterService: BusinessCenterService,
  ) {
    this.accountGroupService.setSortOptions('Created', false);
  }

  lookupAccountsWithoutSnapshots(): Observable<AccountInfo[]> {
    const lookupFilter = new LookupFilter();
    const snapshotFilter = new SnapshotFilter();
    snapshotFilter.noSnapshotsCreated = true;
    lookupFilter.snapshotFilter = snapshotFilter;
    const accounts = this.accountGroupService.lookup(
      new ProjectionFilter({ napData: true }),
      undefined,
      undefined,
      lookupFilter,
      3,
    );
    return this.toAccountInfo(accounts);
  }

  private toAccountInfo(accounts: Observable<AccountGroup[]>): Observable<AccountInfo[]> {
    return accounts.pipe(
      switchMap((accountsPipe) => {
        if (!accountsPipe || accountsPipe.length === 0) {
          return of([]);
        }
        const obs: Observable<AccountInfo>[] = accountsPipe
          .filter((account) => account.napData)
          .map((account) => this.buildAccountInfo(account));
        return combineLatest(obs);
      }),
    );
  }

  private buildAccountInfo(ag: AccountGroup): Observable<AccountInfo> {
    return this.businessCenterService.getVBCEntryUrl(ag.accountGroupId).pipe(
      take(1),
      map((vbcInfo) => {
        return {
          accountGroup: ag,
          vbcEntryInfo: vbcInfo,
        };
      }),
    );
  }
}
